import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { createPortal } from "react-dom";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import "pdfjs-dist/web/pdf_viewer.css";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

// --- SECTION 1: PDF SUB-COMPONENTS & LAYERS ---
import PDFHighlightBrush from "./PDFHighlightBrush";
import SelectionPopup from "./SelectionPopup";
import { buildTextSnippetFromSelection, startTouchSnippetDrag } from "./pdfDragHandlers";
import PDFDrawingLayer from "./PDFDrawingLayer";
import { PDFTextHighlightLayer } from "./PDFTextHighlightLayer";
import PDFConnectionLines from "./PDFConnectionLines";
import PDFScrollMarker from "./PDFScrollMarker";

// --- SECTION 2: CUSTOM PDF HOOKS ---
import { usePdfRenderer } from "./usePdfRenderer";
import { useShrinkExpand } from "./pdfShrinkExpand";
import { useSearchHighlight } from "./pdfSearchHighlight";
import { useSelection } from "./pdfSelection";
import { usePdfText } from "./addpdftext.js";
import { useHighlightLogic } from "./PDFTextHighlightLayer";

// --- SECTION 3: UTILS & HANDLERS ---
import { scrollToSnippet as scrollToSnippetUtil, scrollToPage as scrollToPageUtil } from "./pdfScrollUtils";
import { usePDF } from "../../context/PDFContext";
import { useUI } from "../../context/UIContext";
import { useAppActions } from "../../context/AppContext";
import { useWorkspace } from "../../context/WorkspaceContext";
// --- SECTION 4: LAZY LOADED OVERLAYS ---
const PDFThumbnailView = React.lazy(() => import("./PDFThumbnailView"));

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * PDFViewer Component
 * Manages PDF rendering, interaction layers, and side-features.
 */
const PDFViewer = React.memo(
    forwardRef(
        (
            {
                fileUrl,
                sourcePdfId = null,
                localZoom = null,       // when set, overrides AppContext zoomLevel (independent panel)
                setLocalZoom = null,    // setter for localZoom, so fit-to-width can drive the independent panel
                fitKey = null,          // changes when this panel's available width changes without a
                                        // pixel-width change the ResizeObserver can see (e.g. the side
                                        // panel opening/closing) → forces a re-fit
            },
            ref
        ) => {
            const {
                searchText,
                currentMatchIndex,
                pdfAnnotations,
                highlights,
                bookmarks,
                pdfConnectionLines, setPdfConnectionLines,
                drawingLine, setDrawingLine,
                activePdfConnId,
            } = usePDF();

            const {
                tool,
                showThumbnails, setShowThumbnails,
                zoomLevel: contextZoomLevel, setZoomLevel,
                pdfRenderScale,
                isResizing,
                TOOL_MODES,
                pendingCrossLink, startCrossLink,
                startDragWire,
            } = useUI();

            const {
                handleAddBookmark,
                handleDeleteBookmark,
                completeCrossLink,
            } = useAppActions();

            // Active PDF in context. Annotation state (highlights/drawings/brush) belongs to this
            // PDF, so the interactive layers must only render on the panel showing it — otherwise a
            // second panel with a different PDF would mirror the active PDF's annotations.
            const { pdfId } = useWorkspace();
            const isActivePdf = String(sourcePdfId) === String(pdfId);

            const mode = tool;

            // Use localZoom when provided (right panel independent zoom), else fall back to shared context zoom
            const zoomLevel = localZoom !== null ? localZoom : contextZoomLevel;

            // Cursor style per tool
            const toolCursor = {
                [TOOL_MODES?.PEN]: "crosshair",
                [TOOL_MODES?.ERASER]: "cell",
                [TOOL_MODES?.HIGHLIGHT_BRUSH]: "crosshair",
                [TOOL_MODES?.STICKY_NOTE]: "copy",
                [TOOL_MODES?.DRAW_LINE]: "crosshair",
                [TOOL_MODES?.ADD_BOX]: "crosshair",
                [TOOL_MODES?.PDF_CONNECT]: "crosshair",
            }[tool] || "text";

            // Updated internal handlers
            const onThumbnailsClose = () => setShowThumbnails(false);

            // --- SECTION 5: REFS & CORE STATE ---
            const containerRef = useRef();
            const zoomContentRef = useRef();
            const latestRef = useRef({
                searchText,
                currentMatchIndex,
                mode,
                highlightMatchesOnPage: null,
                pdfAnnotations,
            });

            // PDF connection line drawing refs
            const isDrawing = useRef(false);
            const LINE_COLORS = ["#e53935", "#1e88e5", "#43a047", "#fb8c00", "#8e24aa"];
            const lineColorRef = useRef(0);

            // --- SECTION 6: FEATURE HOOK CALLS ---

            // From pdfShrinkExpand.js
            const {
                shrinkMapRef, getCurrentPageNum, expandAll, shrinkState, contractBetween, dynamicHeight
            } = useShrinkExpand(containerRef, zoomContentRef);

            // From usePdfRenderer.js
            const {
                pdfDocRef, pdfLoaded, renderedPageMap, pdfDimensions
            } = usePdfRenderer({
                fileUrl, containerRef, contentRef: zoomContentRef, latestRef, shrinkState, pdfRenderScale
            });

            // From pdfSearchHighlight.js
            const { highlightMatchesOnPage } = useSearchHighlight(
                containerRef, pdfDocRef, shrinkMapRef, pdfLoaded, zoomContentRef
            );

            // From pdfSelection.js
            const {
                popupData, multiSelections, setMultiSelections, addToMultiSelect, clearPopup: clearSelectionPopup
            } = useSelection(containerRef, mode, zoomContentRef, zoomLevel, sourcePdfId);

            // From usePdfText.js
            const { renderPdfAnnotation, addPdfText: addPdfTextLogic } = usePdfText(
                containerRef, getCurrentPageNum, zoomContentRef
            );

            // From PDFTextHighlightLayer.js
            const { handleHighlight } = useHighlightLogic(
                containerRef, clearSelectionPopup
            );

            // --- SECTION 7: SYNC EFFECTS ---
            useEffect(() => {
                latestRef.current = {
                    ...latestRef.current,
                    searchText,
                    currentMatchIndex,
                    mode,
                    highlightMatchesOnPage,
                    highlights,
                    pdfAnnotations,
                    renderPdfAnnotation,
                };
            }, [searchText, currentMatchIndex, mode, highlightMatchesOnPage, highlights, pdfAnnotations, renderPdfAnnotation]);

            // Zoom-change classifier (race-free). programmaticZoomRef marks a change WE (auto-fit)
            // made; anything else is treated as a manual user zoom, which stops auto-fitting.
            // Flag-based rather than value-diff, because setZoom is async and value comparisons race
            // with the fast frames of the panel's 0.5s width animation.
            const zoomRef = useRef(zoomLevel);
            const programmaticZoomRef = useRef(false);
            const manualZoomRef = useRef(false);
            useEffect(() => {
                if (programmaticZoomRef.current) {
                    programmaticZoomRef.current = false;      // consume our own change
                } else if (zoomRef.current !== zoomLevel) {
                    manualZoomRef.current = true;             // user zoomed → stop auto-fitting
                }
                zoomRef.current = zoomLevel;
            }, [zoomLevel]);

            // Current-value ref for the resizer flag, so the fit callback can read it without
            // re-subscribing. While the divider is being dragged it manages zoom itself.
            const isResizingRef = useRef(isResizing);
            useEffect(() => { isResizingRef.current = isResizing; }, [isResizing]);

            // --- FIT PAGE: scale the zoom so one whole page is visible — it fits the panel's width
            // AND height, whichever is tighter — so the first page is fully readable on open
            // without touching the zoom buttons. The ResizeObserver re-fits across every frame of
            // the panel's width animation, so it settles on the correct size. Backs off once the
            // user manually zooms.
            // Works for both the main panel (setZoomLevel) and the independent right panel (setLocalZoom). ---
            const applyZoom = localZoom !== null ? setLocalZoom : setZoomLevel;
            useEffect(() => {
                if (!applyZoom) return; // no setter available → nothing to drive
                const container = containerRef.current;
                if (!container || !(pdfDimensions.width > 0)) return;

                // New PDF loaded → re-enable auto-fit even if the user had zoomed before.
                // Keyed on fileUrl (not just pdfDimensions.width) because two different PDFs can
                // render at the identical pixel width (e.g. same-size court filings), in which case
                // width alone wouldn't change and a stale manual zoom would wrongly carry over
                // to the new tab.
                manualZoomRef.current = false;
                // Outer box size (offset*, which includes scrollbars) — used only to tell a real
                // panel resize from a scrollbar appearing when the user zooms past the panel width.
                let lastBoxW = container.offsetWidth;
                let lastBoxH = container.offsetHeight;

                const computeFit = () => {
                    if (isResizingRef.current) return; // divider drag manages zoom itself
                    const w = container.clientWidth;
                    const h = container.clientHeight;
                    // A panel-size change (Split/PDF/Notes switch, window resize) makes the previous
                    // zoom stale → re-engage auto-fit for the new size even after a manual zoom.
                    // Compared on the outer box: a horizontal scrollbar from zooming in shrinks
                    // clientHeight but is not a resize, and must not snap the zoom back to fit.
                    const boxW = container.offsetWidth, boxH = container.offsetHeight;
                    if (Math.abs(boxW - lastBoxW) > 1 || Math.abs(boxH - lastBoxH) > 1) manualZoomRef.current = false;
                    lastBoxW = boxW;
                    lastBoxH = boxH;
                    if (manualZoomRef.current) return; // user took over → leave zoom alone
                    // clientWidth excludes a scrollbar once it's present; the 6px margin covers
                    // rounding and the brief moment before the vertical scrollbar appears, so the
                    // page never overflows and clips at the right edge.
                    const avail = w - 6;
                    if (avail <= 0) return;
                    const pageWidth = pdfDimensions.width + 40; // matches centering wrapper padding
                    // pdfDimensions.height is the whole document, so take one page's height from the
                    // first page wrapper (its offsetHeight is unscaled — the zoom is a CSS transform).
                    const pageHeight = container.querySelector(".pdf-page-wrapper")?.offsetHeight || 0;
                    const fitWidth = avail / pageWidth;
                    const fitHeight = pageHeight > 0 ? (h - 6) / (pageHeight + 40) : Infinity;
                    // Floor (not round) so the zoom is never rounded UP past the exact fit.
                    const fit = Math.max(0.3, Math.min(3, Math.floor(Math.min(fitWidth, fitHeight) * 100) / 100));
                    if (Math.abs(zoomRef.current - fit) < 0.001) return; // already fitted
                    programmaticZoomRef.current = true; // mark the next zoom change as ours
                    applyZoom(fit);
                };

                // Measure after the browser has laid out the panel (double rAF), not on the same
                // tick the PDF finished loading — at that point the panel may still be at an
                // interim width, which would compute a too-large fit.
                let raf1 = 0, raf2 = 0;
                raf1 = requestAnimationFrame(() => {
                    raf2 = requestAnimationFrame(computeFit);
                });

                const ro = new ResizeObserver(computeFit);
                ro.observe(container);

                // Extra re-fits as the panel's 0.5s width animation and scrollbar layout settle,
                // in case a size change wasn't observed live.
                const t1 = setTimeout(computeFit, 250);
                const t2 = setTimeout(computeFit, 600);
                return () => {
                    ro.disconnect();
                    cancelAnimationFrame(raf1); cancelAnimationFrame(raf2);
                    clearTimeout(t1); clearTimeout(t2);
                };
                // NOTE: depend on applyZoom (stable setter, flips only when the panel opens/closes),
                // NOT on localZoom — localZoom changes on every zoom and would re-subscribe each time.
                // fileUrl → re-fit when switching to a same-width PDF.
                // fitKey → re-fit when the panel's available width changes without a pixel-width
                //          change the ResizeObserver can see (e.g. the side panel opening/closing).
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [pdfDimensions.width, applyZoom, fileUrl, fitKey]);

            // --- SECTION 8: PAGE ANCHOR HELPERS (also exposed via imperative handle) ---
            const getAnchorScreenPos = (pageNum, xPct, yPct) => {
                const content = zoomContentRef.current || containerRef.current;
                if (!content) return null;
                const pageEl = content.querySelector(`.pdf-page-wrapper[data-page-number="${pageNum}"]`);
                if (!pageEl) return null;
                const canvas = pageEl.querySelector("canvas");
                const el = canvas || pageEl;
                const rect = el.getBoundingClientRect();
                return {
                    x: rect.left + (xPct || 0.5) * rect.width,
                    y: rect.top + (yPct || 0.5) * rect.height,
                };
            };

            const getPageAnchorFromScreen = (screenX, screenY) => {
                const content = zoomContentRef.current || containerRef.current;
                if (!content) return null;
                const pages = content.querySelectorAll(".pdf-page-wrapper[data-page-number]");
                for (const pageEl of pages) {
                    const canvas = pageEl.querySelector("canvas");
                    const el = canvas || pageEl;
                    const rect = el.getBoundingClientRect();
                    if (screenX >= rect.left && screenX <= rect.right &&
                        screenY >= rect.top && screenY <= rect.bottom) {
                        const pageNum = parseInt(pageEl.dataset.pageNumber, 10);
                        return {
                            pageNum,
                            xPct: Math.max(0, Math.min(1, (screenX - rect.left) / rect.width)),
                            yPct: Math.max(0, Math.min(1, (screenY - rect.top) / rect.height)),
                        };
                    }
                }
                return null;
            };

            useImperativeHandle(ref, () => ({
                scrollToSnippet(snippet) {
                    scrollToSnippetUtil(containerRef.current, snippet, pdfDocRef.current, { isResizing, scale: pdfRenderScale });
                },
                getCurrentPageNum() {
                    return getCurrentPageNum();
                },
                getLatestSelection() {
                    const current = containerRef.current._lastSelection;
                    if (multiSelections.length > 0) {
                        return current ? [...multiSelections, current] : multiSelections;
                    }
                    return current;
                },
                clearSelection() {
                    const selection = window.getSelection();
                    if (selection) selection.removeAllRanges();
                    containerRef.current._lastSelection = null;
                    setMultiSelections([]);
                    clearSelectionPopup();
                },
                getAnchorScreenPos,
                getPageAnchorFromScreen,
                scrollToPage(pageNum) {
                    //pdfscrollutils.js
                    scrollToPageUtil(containerRef.current, pageNum, pdfDocRef.current, {
                        shrinkState,
                        expandAll,
                        scale: pdfRenderScale
                    });
                },
                pdfDoc: pdfDocRef.current,
                getTotalPages() { return pdfDocRef.current?.numPages || 0; },
                contractBetweenPages(p1, p2) {
                    contractBetween(p1, p2);
                },
                expandAllPages() {
                    expandAll();
                },
                addPdfText() {
                    addPdfTextLogic();
                },
                async extractAllText() {
                    // Wait up to 15s for PDF to finish loading
                    let doc = pdfDocRef.current;
                    if (!doc) {
                        for (let i = 0; i < 30 && !pdfDocRef.current; i++) {
                            await new Promise(r => setTimeout(r, 500));
                        }
                        doc = pdfDocRef.current;
                    }
                    if (!doc) return "";
                    const parts = [];
                    for (let i = 1; i <= doc.numPages; i++) {
                        const page = await doc.getPage(i);
                        let pageText = "";

                        // 1) Try regular content stream text
                        const content = await page.getTextContent();
                        for (const item of content.items) {
                            if (!item.str) continue;
                            pageText += item.str;
                            if (item.hasEOL) pageText += "\n";
                            else pageText += " ";
                        }

                        // 2) If empty, try form field annotations (AcroForm / court system PDFs)
                        if (!pageText.trim()) {
                            try {
                                const annotations = await page.getAnnotations();
                                for (const ann of annotations) {
                                    // fieldValue = user-entered data, alternativeText = label
                                    if (ann.fieldValue) pageText += `${ann.fieldName || ""}: ${ann.fieldValue}\n`;
                                    else if (ann.alternativeText) pageText += ann.alternativeText + "\n";
                                    else if (ann.contents) pageText += ann.contents + "\n";
                                }
                            } catch (e) { /* ignore */ }
                        }

                        pageText = pageText.trim();
                        if (pageText) parts.push(`[Page ${i}]\n${pageText}`);
                    }
                    const fullText = parts.join("\n\n");
                    console.log(`[extractAllText] Extracted ${fullText.length} chars from ${doc.numPages} pages`);
                    return fullText;
                }
            }));

            // --- SECTION 8b: PDF CONNECTION LINE HANDLERS ---
            // Lines are stored as { from: {pageNum,xPct,yPct}, to: {pageNum,xPct,yPct} }.
            // getAnchorScreenPos() re-queries getBoundingClientRect each render, so lines
            // automatically follow collapsed/zoomed/scrolled pages.
            const pdfConnectDownRef = useRef(null);

            pdfConnectDownRef.current = (e) => {
                if (tool !== TOOL_MODES?.PDF_CONNECT) return;
                if (e.pointerType === 'mouse' && e.button !== 0) return;
                // Let sticky notes / popups / buttons keep working in this mode
                if (e.target.closest?.('[data-annot-id], .selection-popup, button')) return;
                e.preventDefault();
                e.stopPropagation();
                const pointerId = e.pointerId;

                const startScreen = { x: e.clientX, y: e.clientY };
                const startAnchor = getPageAnchorFromScreen(e.clientX, e.clientY);
                const color = LINE_COLORS[lineColorRef.current % LINE_COLORS.length];

                isDrawing.current = true;
                setDrawingLine({ id: 'tmp', startScreen, endScreen: startScreen, color });

                const onMove = (ev) => {
                    if (!isDrawing.current || ev.pointerId !== pointerId) return;
                    setDrawingLine(prev => prev ? { ...prev, endScreen: { x: ev.clientX, y: ev.clientY } } : null);
                };

                const onUp = (ev) => {
                    if (ev.pointerId !== pointerId) return;
                    isDrawing.current = false;
                    window.removeEventListener('pointermove', onMove);
                    window.removeEventListener('pointerup', onUp);
                    window.removeEventListener('pointercancel', onUp);
                    if (ev.type === 'pointercancel') { setDrawingLine(null); return; }

                    const dist = Math.hypot(ev.clientX - startScreen.x, ev.clientY - startScreen.y);
                    if (dist < 8) { setDrawingLine(null); return; }

                    const endAnchor = getPageAnchorFromScreen(ev.clientX, ev.clientY);
                    // Require at least one endpoint on a page
                    if (!startAnchor && !endAnchor) { setDrawingLine(null); return; }

                    // Fall back to nearest page if one side misses
                    const from = startAnchor || endAnchor;
                    const to = endAnchor || startAnchor;

                    const lineColor = LINE_COLORS[lineColorRef.current % LINE_COLORS.length];
                    lineColorRef.current += 1;
                    setPdfConnectionLines(prev => [...prev, {
                        id: `pdfconn-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                        from, to, color: lineColor, strokeWidth: 2,
                    }]);
                    setDrawingLine(null);
                };

                // Pointer events: mouse, finger and Apple Pencil all draw the line
                window.addEventListener('pointermove', onMove);
                window.addEventListener('pointerup', onUp);
                window.addEventListener('pointercancel', onUp);
            };

            useEffect(() => {
                const container = containerRef.current;
                if (!container || tool !== TOOL_MODES?.PDF_CONNECT) return;
                const handler = (e) => pdfConnectDownRef.current?.(e);
                container.addEventListener('pointerdown', handler, { capture: true });
                // Touch / Pencil: drawing a line must not scroll the PDF instead
                const prevTouchAction = container.style.touchAction;
                container.style.touchAction = 'none';
                return () => {
                    container.removeEventListener('pointerdown', handler, { capture: true });
                    container.style.touchAction = prevTouchAction;
                };
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [tool]);

            // Auto-collapse pages between newly created connection lines
            const prevConnLinesLenRef = useRef(0);
            useEffect(() => {
                const lines = pdfConnectionLines || [];
                if (lines.length > prevConnLinesLenRef.current) {
                    const newLine = lines[lines.length - 1];
                    if (newLine && newLine.from?.pageNum !== newLine.to?.pageNum) {
                        // RAF ensures DOM page wrappers are fully laid out before collapsing
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                contractBetween(newLine.from.pageNum, newLine.to.pageNum);
                            });
                        });
                    }
                }
                prevConnLinesLenRef.current = lines.length;
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [pdfConnectionLines]);

            // --- SECTION 9: FINAL UI RENDER ---
            const effectiveHeight = (dynamicHeight > 0) ? dynamicHeight : pdfDimensions.height;

            // Floating zoom pill button style (used by the in-view zoom control below)
            const zoomPillBtn = {
                width: 30, height: 30, borderRadius: "50%", border: "none",
                background: "transparent", color: "#334155", fontSize: 18, fontWeight: 700,
                lineHeight: 1, cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center",
            };

            return (
                <div className="pdf-viewer-outer-wrapper" style={{ position: "relative", width: "100%", flex: 1, minHeight: 0 }}>

                    {/* [BASE LAYER] Scrollable PDF Container - Handled by pdfDragHandlers.js */}
                    <div ref={containerRef} className="pdf-viewer-container"
                        style={{
                            width: "100%", height: "100%", overflow: "auto", position: "relative",
                            backgroundColor: "#e8eaed",
                            userSelect: (tool === "pen" || tool === "eraser") ? "none" : "text",
                            cursor: toolCursor,
                            scrollBehavior: "auto",
                        }}
                    >
                        <div className="pdf-zoom-centering-wrapper" style={{
                            width: pdfDimensions.width > 0 ? (pdfDimensions.width + 40) * zoomLevel : "100%",
                            height: effectiveHeight > 0 ? (effectiveHeight + 40) * zoomLevel : "100%",
                            // Centre the page when it's narrower than the panel (fit-page leaves side
                            // space); auto margins fall to 0 once zoomed wider, so h-scroll still works.
                            margin: "0 auto",
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            position: "relative",
                            overflow: "visible", // 🚀 Allow drawing handles to overflow if needed
                            pointerEvents: (tool === "pen" || tool === "eraser") ? "auto" : "auto",
                            zIndex: (tool === "pen" || tool === "eraser") ? 200 : 1
                        }}>
                            <div
                                ref={zoomContentRef}
                                className="pdf-zoom-content"
                                style={{
                                    transform: `scale(${zoomLevel})`,
                                    // Read by PDF sticky notes so they don't shrink below real size
                                    "--pdf-zoom": zoomLevel,
                                    transformOrigin: "top left",
                                    transition: isResizing ? "none" : "transform 0.2s ease",
                                    willChange: isResizing ? "transform" : "auto",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    padding: "20px 20px",
                                    width: "fit-content",
                                    position: "relative",
                                }}
                            />
                        </div>
                    </div>

                    {/* [FLOATING ZOOM] In-view zoom control, bottom-right of the PDF area.
                        Mirrors the old navbar zoom; only shown on the main (context-driven)
                        panel and once a PDF is loaded. */}
                    {localZoom === null && pdfDimensions.width > 0 && (
                        <div className="pdf-floating-zoom" style={{
                            // Sits at bottom: 64 to clear the PDF/Split/Notes toggle that floats in
                            // the bottom-right corner (App.js) when the PDF fills the screen.
                            // zIndex above the page layer, which is raised to 200 in pen/eraser
                            // mode — otherwise the page covered the pill and zoom stopped working.
                            position: "absolute", bottom: 64, right: 18, zIndex: 250,
                            display: "flex", alignItems: "center", gap: 2,
                            background: "rgba(255,255,255,0.92)",
                            backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                            border: "1px solid rgba(0,0,0,0.1)", borderRadius: 999,
                            padding: "3px 5px", boxShadow: "0 4px 16px rgba(0,0,0,0.16)",
                            userSelect: "none",
                        }}>
                            <button
                                type="button"
                                onClick={() => setZoomLevel(prev => Math.max(0.3, parseFloat((prev - 0.15).toFixed(2))))}
                                title="Zoom Out (-)"
                                style={zoomPillBtn}
                            >−</button>
                            <span
                                onClick={() => setZoomLevel(1)}
                                title="Reset to 100%"
                                style={{ minWidth: 48, textAlign: "center", fontSize: 12.5, fontWeight: 600, color: "#334155", cursor: "pointer" }}
                            >{Math.round((zoomLevel || 1) * 100)}%</span>
                            <button
                                type="button"
                                onClick={() => setZoomLevel(prev => Math.min(3, parseFloat((prev + 0.15).toFixed(2))))}
                                title="Zoom In (+)"
                                style={zoomPillBtn}
                            >+</button>
                        </div>
                    )}

                    {/* [SCROLL MARKER] Bookmark, highlight & connection line flags on the scrollbar */}
                    <PDFScrollMarker
                        bookmarks={bookmarks}
                        highlights={highlights}
                        pdfConnectionLines={pdfConnectionLines}
                        containerRef={containerRef}
                        zoomContentRef={zoomContentRef}
                        onDeleteBookmark={handleDeleteBookmark}
                        onDeleteLine={(id) => setPdfConnectionLines(prev => prev.filter(l => l.id !== id))}
                    />

                    {/* [CONNECTION LINES LAYER] Screen-coord SVG overlay — works after collapse/zoom/scroll */}
                    <PDFConnectionLines
                        lines={pdfConnectionLines}
                        drawingLine={drawingLine}
                        getAnchorScreenPos={getAnchorScreenPos}
                        containerRef={containerRef}
                        externalActiveId={activePdfConnId}
                    />

                    {/* [TOOL LAYER] Expand All Button - Logic from pdfShrinkExpand.js */}
                    {shrinkState && (
                        <button
                            className="pdf-expand-all-btn"
                            onClick={expandAll}
                            style={{
                                position: "absolute", left: "10px", zIndex: 201, cursor: "pointer",
                                top: shrinkState === "top" ? "20%" : shrinkState === "bottom" ? "80%" : "50%",
                                width: "30px", height: "120px", transform: "translateY(-50%)",
                                backgroundColor: "#4a90e2", color: "white", border: "none",
                                borderRadius: "0 8px 8px 0", boxShadow: "2px 0 8px rgba(0,0,0,0.2)",
                                transition: "width 0.2s, top 0.3s ease", display: "flex", alignItems: "center", justifyContent: "center"
                            }}
                            title="Expand All"
                        >
                            <div style={{ writingMode: "vertical-rl", fontSize: "20px", fontWeight: "bold" }}>⬍</div>
                        </button>
                    )}

                    {/* [INTERACTION LAYER] Selection Popup - Component from SelectionPopup.js */}
                    {popupData && (
                        <SelectionPopup
                            onHighlight={handleHighlight}
                            position={popupData.position}
                            onSelectMore={addToMultiSelect}
                            showSelectMore={multiSelections.length === 0}
                            onLink={() => {
                                window.dispatchEvent(new CustomEvent("request-link-selection"));
                                clearSelectionPopup();
                            }}
                            onClose={clearSelectionPopup}
                            onBookmark={() => {
                                const text = popupData.selectedText || "";
                                const pg = popupData.pageNum || getCurrentPageNum() || 1;
                                const name = text.trim().slice(0, 80) || `Page ${pg}`;
                                return handleAddBookmark(pg, name);
                            }}
                            hasPendingCrossLink={!!(pendingCrossLink && (
                                String(pendingCrossLink.pdfId) !== String(sourcePdfId) ||
                                pendingCrossLink.pageNum !== (popupData.pageNum || 1)
                            ))}
                            onConnectPdf={() => {
                                const endpoint = {
                                    pdfId: sourcePdfId,
                                    pageNum: popupData.pageNum || 1,
                                    xPct: popupData.anchorXPct ?? 0.5,
                                    yPct: popupData.anchorYPct ?? 0.5,
                                    text: (popupData.selectedText || "").slice(0, 120),
                                };
                                const isDifferentTarget = pendingCrossLink && (
                                    String(pendingCrossLink.pdfId) !== String(sourcePdfId) ||
                                    pendingCrossLink.pageNum !== endpoint.pageNum
                                );
                                if (isDifferentTarget) {
                                    completeCrossLink(endpoint);
                                } else {
                                    startCrossLink(endpoint);
                                }
                                clearSelectionPopup();
                            }}
                            onDragWireStart={(mouseX, mouseY) => {
                                const endpoint = {
                                    pdfId: sourcePdfId,
                                    pageNum: popupData.pageNum || 1,
                                    xPct: popupData.anchorXPct ?? 0.5,
                                    yPct: popupData.anchorYPct ?? 0.5,
                                    text: (popupData.selectedText || "").slice(0, 120),
                                };
                                startDragWire(endpoint, mouseX, mouseY);
                                clearSelectionPopup();
                            }}
                            onDragSnippetStart={(x, y, sourceEl) => {
                                // Prefer the live selection; iOS may already have dropped it
                                // when the chip was touched, so fall back to the stored copy.
                                const last = containerRef.current?._lastSelection;
                                const snippet = buildTextSnippetFromSelection(containerRef, zoomLevel, sourcePdfId)
                                    || (popupData.selectedText ? {
                                        ...(last || {}),
                                        id: Date.now(),
                                        type: "text",
                                        text: popupData.selectedText,
                                        pageNum: popupData.pageNum || last?.pageNum,
                                        fromPDF: true,
                                        ...(sourcePdfId != null && { pdf_id: sourcePdfId }),
                                    } : null);
                                if (!snippet) return;
                                startTouchSnippetDrag(snippet, x, y, sourceEl);
                                clearSelectionPopup();
                            }}
                        />
                    )}

                    {/* [PAGE LAYERS] Rendering Layers into Page Portals.
                        Only the panel showing the active PDF renders the annotation layers —
                        they read shared context state, so rendering them on a second panel with a
                        different PDF would mirror the active PDF's drawings/highlights onto it. */}
                    {Object.entries(renderedPageMap).map(([pageNum, info]) => (
                        <React.Fragment key={`portal-${pageNum}`}>
                            {/* Handled by PDFDrawingLayer.js */}
                            {isActivePdf && createPortal(
                                <PDFDrawingLayer
                                    pageNum={parseInt(pageNum, 10)}
                                    width={info.wrapper.clientWidth} height={info.wrapper.clientHeight}
                                    tool={tool}
                                    zoomLevel={zoomLevel}
                                    isResizing={isResizing}
                                />, info.wrapper
                            )}

                            {/* Handled by PDFTextHighlightLayer.js */}
                            {isActivePdf && createPortal(<PDFTextHighlightLayer pageNum={parseInt(pageNum, 10)} />, info.wrapper)}

                            {/* Handled by PDFHighlightBrush.js */}
                            {isActivePdf && createPortal(
                                <PDFHighlightBrush
                                    pageNum={parseInt(pageNum, 10)}
                                    width={info.wrapper.clientWidth} height={info.wrapper.clientHeight}
                                    zoomLevel={zoomLevel}
                                    isResizing={isResizing}
                                />, info.wrapper
                            )}

                            {/* Page number badge — top-right corner of each page */}
                            {createPortal(
                                <div style={{
                                    position: "absolute", top: 6, right: 8,
                                    background: "rgba(0,0,0,0.28)",
                                    color: "rgba(5, 5, 5, 0.92)",
                                    fontSize: 17, fontWeight: 600,
                                    padding: "2px 7px", borderRadius: 5,
                                    pointerEvents: "none", zIndex: 600,
                                    letterSpacing: "0.03em", userSelect: "none",
                                }}>
                                    {pageNum}
                                </div>, info.wrapper
                            )}
                        </React.Fragment>
                    ))}

                    {/* PageJump is now inline in Navbar — no modal here */}

                    {showThumbnails && pdfDocRef.current && (
                        <React.Suspense fallback={<div className="pdf-thumbnail-overlay">Loading...</div>}>
                            <PDFThumbnailView
                                pdfDoc={pdfDocRef.current}
                                onClose={onThumbnailsClose}
                                onPageClick={(n) => { ref.current?.scrollToPage(n); onThumbnailsClose(); }}
                            />
                        </React.Suspense>
                    )}
                </div>
            );
        }
    )
);

export default PDFViewer;
